import React from 'react';
import { Button, Col, Row, Panel } from 'react-bootstrap';
import * as faIcon from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AscComponent from '../../components/AscComponent'
import AscModal from "../../containers/Elements/Modal/Modal";
import Favi from '../../containers/Elements/Favi/Favi';
import InboundGroupModal from '../../containers/InboundGroup/InboundGroupModal';
import ExternalNumberModal from '../../containers/ExternalNumber/ExternalNumberModal';
import ExtensionNumberModal from '../../containers/ExtensionNumber/ExtensionNumberModal';
import { CSVLink } from "react-csv";
import moment from "moment";
import * as GlobalConst from "../../components/AscConstants";

/**
 * 番号設定構成図の共通コンポーネント
 */
class NumberSettingInfoDiagram extends AscComponent {
    constructor(props) {
        super(props);
        this.state = {
            // 子モーダルの状態管理
            showExternalNumberModal: false,
            externalNumberModalLoading: false,
            showInboundGroupModal: false,
            inboundGroupModalLoading: false,
            showExtensionNumberModal: false,
            extensionNumberModalLoading: false,

            // 各モーダル用のデータ
            externalNumberModalData: null,
            inboundGroupModalData: null,
            extensionNumberModalData: null,

            // CSV出力用の状態
            csvData: [],
            csvHeader: [],
            csvLoading: false,

            // 呼び出し元画面の情報
            sourceScreen: null
        };

        // CSVダウンロード用のref
        this.csvLink = React.createRef();
    }

    componentDidMount = () => {
        // モーダルが開かれた後に接続線を描画
        this.scheduleDrawConnections();
    }

    componentDidUpdate = (prevProps) => {
        // 番号設定構成図データが更新された時に接続線を再描画
        const { state } = this.props;
        const prevState = prevProps.state;

        // モーダルが開かれた時またはデータが読み込まれた時
        if (state.showResourceMap && (
            (!prevState.showResourceMap) || // モーダルが開かれた
            (state.resourceMapData !== prevState.resourceMapData) || // データが更新された
            (prevState.resourceMapLoading && !state.resourceMapLoading) // 読み込み完了
        )) {
            this.scheduleDrawConnections();
        }
    }

    scheduleDrawConnections = () => {
        const { state } = this.props;
        const DRAW_CONNECTION_DELAY = 500;

        // データが完全に読み込まれている場合のみ実行
        if (state.showResourceMap &&
            !state.resourceMapLoading &&
            state.resourceMapData) {

            setTimeout(() => {
                this.drawConnections();
            }, DRAW_CONNECTION_DELAY);
        }
    }

    drawConnections = () => {
        try {
            const { connectionLineClass } = this.props;
            const svg = document.querySelector(`.${connectionLineClass}`);
            const DRAW_CONNECTION_DELAY = 1000;
            if (!svg) {
                // 少し待ってから再試行
                setTimeout(() => {
                    const retrySvg = document.querySelector(`.${connectionLineClass}`);
                    if (retrySvg) {
                        this.drawConnectionsInternal(retrySvg);
                    }
                }, DRAW_CONNECTION_DELAY);
                return;
            }

            this.drawConnectionsInternal(svg);
        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.drawConnectionError, error);
        }
    };

    drawConnectionsInternal = (svg) => {
        try {
            // 既存の線をクリア
            while (svg.firstChild) {
                svg.removeChild(svg.firstChild);
            }

            const { tableClass } = this.props;
            const table = document.querySelector(`.${tableClass}`);
            if (!table) return;

            const svgRect = svg.getBoundingClientRect();

            // 接続線描画ロジック（共通）
            this.drawConnectionLogicCommon(svg, svgRect, table);

        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.drawConnectionError, error);
        }
    };

    /**
     * 3画面共通の接続線描画
     * @param {SVGSVGElement} svg 接続線を描画するSVG要素
     * @param {DOMRect} svgRect svg.getBoundingClientRect() の結果。画面座標→SVG座標の変換に使用
     * @param {HTMLTableElement} table テーブル要素(列境界位置を推定するため使用)
     */
    drawConnectionLogicCommon = (svg, svgRect, table) => {
        const source = this.props.sourceScreen;

        // 列境界（テーブルのボーダー位置）
        let borderExternalGroupX = null; // 外線番号列と内線グループ列の境界
        let borderGroupExtensionX = null; // 内線グループ列と内線番号列の境界
        try {
            const firstRow = table && table.querySelector('tbody tr');
            if (firstRow) {
                const tds = firstRow.children;
                if (tds && tds.length >= 2) {
                    borderExternalGroupX = Math.round(tds[0].getBoundingClientRect().right - svgRect.left);
                }
                if (tds && tds.length >= 3) {
                    borderGroupExtensionX = Math.round(tds[1].getBoundingClientRect().right - svgRect.left);
                }
            }
        } catch (e) {
            // noop: 取得できない場合は中間点で描画
        }

        // 線描画関数
        const createLine = (x1, y1, x2, y2) => {
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', Math.round(x1));
            line.setAttribute('y1', Math.round(y1));
            line.setAttribute('x2', Math.round(x2));
            line.setAttribute('y2', Math.round(y2));
            line.setAttribute('stroke', '#8e9ba7');
            line.setAttribute('stroke-width', '3');
            svg.appendChild(line);
        };

        if (source === 'external') {
            const externalBox = document.querySelector('.external-resource-box');
            const groupBoxes = document.querySelectorAll('.external-group-box');
            const extensionBoxes = document.querySelectorAll('.external-extension-box');
            if (!externalBox || groupBoxes.length === 0) return;

            // 外線番号ボックスの座標取得(線の起点となるボックスの位置)
            const externalRect = externalBox.getBoundingClientRect();

            // 外線番号→各内線グループ
            groupBoxes.forEach(groupBox => {
                const groupRect = groupBox.getBoundingClientRect();
                const startX = Math.round(externalRect.right - svgRect.left); // 線の開始地点(X座標)
                const startY = Math.round(externalRect.top + externalRect.height / 2 - svgRect.top); // 線の開始地点(Y座標)
                const endX = Math.round(groupRect.left - svgRect.left); // 線の接続先(各内線グループ)のX座標
                const endY = Math.round(groupRect.top + groupRect.height / 2 - svgRect.top); // 線の接続先(各内線グループ)のY座標
                const midX = startX + (endX - startX) / 2; // 外線番号と内線グループの中間位置
                const trunkX = borderExternalGroupX !== null ? borderExternalGroupX : midX; // 外線番号列と内線グループ列の境界(borderExternalGroupXが取得できない場合midXを使用)
                createLine(startX, startY, trunkX, startY); // 外線番号ボックス→列の境界の水平線描画
                createLine(trunkX, startY, trunkX, endY); // 内線グループボックスのY座標へ垂直線描画
                createLine(trunkX, endY, endX, endY); // 内線グループボックスの左端へ水平線描画
            });

            // 各内線グループ→各内線番号
            groupBoxes.forEach(groupBox => {
                const groupId = groupBox.getAttribute('data-group-id');
                const exts = Array.from(extensionBoxes).filter(ext => ext.getAttribute('data-group-id') === groupId);
                const groupRect = groupBox.getBoundingClientRect();
                exts.forEach(ext => {
                    const extRect = ext.getBoundingClientRect();
                    const startX = Math.round(groupRect.right - svgRect.left); // 線の開始地点(X座標)
                    const startY = Math.round(groupRect.top + groupRect.height / 2 - svgRect.top); // 線の開始地点(Y座標)
                    const endX = Math.round(extRect.left - svgRect.left); // 線の接続先(各内線番号)のX座標
                    const endY = Math.round(extRect.top + extRect.height / 2 - svgRect.top); // 線の接続先(各内線番号)のY座標
                    const midX = startX + (endX - startX) / 2; // 内線グループと内線番号の中間位置
                    const trunkX = borderGroupExtensionX !== null ? borderGroupExtensionX : midX; // 内線グループ列と内線番号列の境界(borderGroupExtensionXが取得できない場合midXを使用)
                    createLine(startX, startY, trunkX, startY); // 内線グループボックス→列の境界の水平線描画
                    createLine(trunkX, startY, trunkX, endY); // 内線番号ボックスのY座標へ垂直線描画
                    createLine(trunkX, endY, endX, endY); // 内線番号ボックスの左端へ水平線描画
                });
            });
            return;
        }

        if (source === 'inbound') {
            const externalBoxes = document.querySelectorAll('.inbound-external-box');
            const groupBox = document.querySelector('.inbound-group-resource-box');
            const extensionBoxes = document.querySelectorAll('.inbound-extension-box');
            if (!groupBox) return;
            const groupRect = groupBox.getBoundingClientRect();

            // 各外線番号→内線グループ
            if (externalBoxes.length > 0) {
                externalBoxes.forEach(externalBox => {
                    const externalRect = externalBox.getBoundingClientRect();
                    const startX = Math.round(externalRect.right - svgRect.left); // 線の開始地点(X座標)
                    const startY = Math.round(externalRect.top + externalRect.height / 2 - svgRect.top); // 線の開始地点(Y座標)
                    const endX = Math.round(groupRect.left - svgRect.left); // 線の接続先(各内線グループ)のX座標
                    const endY = Math.round(groupRect.top + groupRect.height / 2 - svgRect.top); // 線の接続先(各内線グループ)のY座標
                    const midX = startX + (endX - startX) / 2; // 外線番号と内線グループの中間位置
                    const trunkX = borderExternalGroupX !== null ? borderExternalGroupX : midX; // 外線番号列と内線グループ列の境界(borderExternalGroupXが取得できない場合midXを使用)
                    createLine(startX, startY, trunkX, startY); // 外線番号ボックス→列の境界の水平線描画
                    createLine(trunkX, startY, trunkX, endY); // 内線グループボックスのY座標へ垂直線描画
                    createLine(trunkX, endY, endX, endY); // 内線グループボックスの左端へ水平線描画
                });
            }

            // 内線グループ→各内線番号
            extensionBoxes.forEach(ext => {
                const extRect = ext.getBoundingClientRect();
                const startX = Math.round(groupRect.right - svgRect.left); // 線の開始地点(X座標)
                const startY = Math.round(groupRect.top + groupRect.height / 2 - svgRect.top); // 線の開始地点(Y座標)
                const endX = Math.round(extRect.left - svgRect.left); // 線の接続先(内線番号)のX座標
                const endY = Math.round(extRect.top + extRect.height / 2 - svgRect.top); // 線の接続先(内線番号)のY座標
                const midX = startX + (endX - startX) / 2; // 内線グループと内線番号の中間位置
                const trunkX = borderGroupExtensionX !== null ? borderGroupExtensionX : midX; // 内線グループ列と内線番号列の境界(borderGroupExtensionXが取得できない場合midXを使用)
                createLine(startX, startY, trunkX, startY); // 内線グループボックス→列の境界の水平線描画
                createLine(trunkX, startY, trunkX, endY); // 各内線番号ボックスのY座標へ垂直線描画
                createLine(trunkX, endY, endX, endY); // 内線番号ボックスの左端へ水平線描画
            });
            return;
        }

        if (source === "extension") {
            const extensionBox = document.querySelector('.extension-box');
            const groupBoxes = document.querySelectorAll('.group-box');
            const externalBoxes = document.querySelectorAll('.external-box');
            if (!extensionBox || groupBoxes.length === 0) return;

            // 内線番号ボックスの座標取得(線の起点となるボックスの位置)
            const extensionRect = extensionBox.getBoundingClientRect();

            const groupToExternals = new Map();
            // 各外線番号ボックスを、紐づく内線グループIDごとにグルーピング
            externalBoxes.forEach(external => {
                const gid = external.getAttribute('data-group-id');
                if (!groupToExternals.has(gid)) groupToExternals.set(gid, []);
                groupToExternals.get(gid).push(external);
            });

            // 各外線番号→各内線グループ
            groupToExternals.forEach((externals, gid) => {
                const group = Array.from(groupBoxes).find(g => g.getAttribute('data-group-id') === gid);
                if (!group || externals.length === 0) return;
                const groupRect = group.getBoundingClientRect();
                const groupLeftX = Math.round(groupRect.left - svgRect.left); // 線の接続先(各内線グループ)のX座標
                const groupCenterY = Math.round(groupRect.top + groupRect.height / 2 - svgRect.top); // 線の接続先(各内線グループ)のY座標
                const firstExternalRect = externals[0].getBoundingClientRect();
                const firstExternalEndX = Math.round(firstExternalRect.right - svgRect.left); // 外線番号の線の開始地点(X座標)
                const midX = firstExternalEndX + (groupLeftX - firstExternalEndX) / 2; // 先頭の外線番号と内線グループの中間位置
                const branchX = borderExternalGroupX !== null ? borderExternalGroupX : midX; // 外線番号列と内線グループ列の中間位置(borderExternalGroupXが取得できない場合midXを使用)

                // 外線番号ボックス群の上下端の位置を取得
                const externalYs = externals.map(ex => {
                    const r = ex.getBoundingClientRect();
                    return Math.round(r.top + r.height / 2 - svgRect.top);
                });
                const minY = Math.min(...externalYs);
                const maxY = Math.max(...externalYs);

                // 各外線番号から先頭行の列の中間位置まで水平線を描画
                externals.forEach(ex => {
                    const r = ex.getBoundingClientRect();
                    const y = Math.round(r.top + r.height / 2 - svgRect.top);
                    const endX = Math.round(branchX);
                    const startX = Math.round(r.right - svgRect.left);
                    createLine(startX, y, endX, y);
                });
                if (externals.length > 1) {
                    // 外線番号が2個以上ある場合のみ、中間位置に垂直線を描画
                    createLine(branchX, Math.min(groupCenterY, minY), branchX, Math.max(groupCenterY, maxY));
                }
                // 先頭行の列の中間位置から内線グループまで水平線を描画
                createLine(branchX, groupCenterY, groupLeftX, groupCenterY);
            });

            // 各内線グループ→各内線番号
            const extensionCenterX = Math.round(extensionRect.left - svgRect.left); // 線の開始地点(x座標)
            const extensionCenterY = Math.round(extensionRect.top + extensionRect.height / 2 - svgRect.top); // 線の開始地点(y座標)
            if (groupBoxes.length > 0) {
                const firstGroupRect = groupBoxes[0].getBoundingClientRect();
                const firstGroupEndX = Math.round(firstGroupRect.right - svgRect.left);
                const midX = firstGroupEndX + (extensionCenterX - firstGroupEndX) / 2; // 内線グループと内線番号の中間位置
                const branchX = borderGroupExtensionX !== null ? borderGroupExtensionX : midX; // 内線グループ列と内線番号列の境界(borderGroupExtensionXが取得できない場合midXを使用)

                // 内線グループボックス群の上下端の位置を取得
                const groupYs = Array.from(groupBoxes).map(g => {
                    const r = g.getBoundingClientRect();
                    return Math.round(r.top + r.height / 2 - svgRect.top);
                });
                const minY = Math.min(...groupYs);
                const maxY = Math.max(...groupYs);

                // 各内線グループから先頭行の列の中間位置まで水平線を描画
                Array.from(groupBoxes).forEach(g => {
                    const r = g.getBoundingClientRect();
                    const y = Math.round(r.top + r.height / 2 - svgRect.top);
                    const startX = Math.round(r.right - svgRect.left);
                    createLine(startX, y, branchX, y);
                });
                if (groupBoxes.length > 1) {
                    // 内線グループが2個以上ある場合のみ、中間位置に垂直線を描画
                    createLine(branchX, Math.min(extensionCenterY, minY), branchX, Math.max(extensionCenterY, maxY));
                }
                // 先頭行の列の中間位置から内線番号まで水平線を描画
                createLine(branchX, extensionCenterY, extensionCenterX, extensionCenterY);
            }
        }
    }

    // 3画面共通のテーブル描画関数（外線番号/内線グループ/内線番号）
    renderTableContentCommon = ({ resourceMapData, tableClass, connectionLineClass }) => {
        const { sourceScreen } = this.props;

        // 共通ヘッダ
        const renderHeader = () => (
            <thead>
                <tr>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border">{this.props.langText.Body.ExternalNumber}</th>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border">{this.props.langText.Body.InboundGroupName}</th>
                    <th className="number-setting-info-diagram-th">{this.props.langText.Body.ExtensionNumber}</th>
                </tr>
            </thead>
        );

        // 外線番号管理画面のボディ描画
        const renderBodyForExternal = () => {
            const rows = [];
            let rowIndex = 0;
            const inboundGroups = resourceMapData.inboundGroups || [];

            if (inboundGroups.length === 0) {
                // 外線番号に設定されている内線グループが0個の場合
                rows.push(
                    <tr key={"no-groups"}>
                        <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border">
                            <div
                                className="external-resource-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px margin-top-2-px"
                            >
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <FontAwesomeIcon
                                        className="sidebar-icon fa-fw"
                                        icon={faIcon.faFax}
                                        size="lg"
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis">{(resourceMapData.externalNumbers && resourceMapData.externalNumbers[0] && resourceMapData.externalNumbers[0].display_number)}</div>
                                        {resourceMapData.externalNumbers && resourceMapData.externalNumbers[0] && resourceMapData.externalNumbers[0].memo && (
                                            <div
                                                className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                title={resourceMapData.externalNumbers[0].memo}
                                            >({resourceMapData.externalNumbers[0].memo})</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border" />
                        <td className="number-setting-info-diagram-td font-weight-bold text-16-px" style={{ color: "#495057", position: "relative" }} />
                    </tr>
                );
                rows.push(
                    <tr key={"spacer-row"}>
                        <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                        <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                        <td className="number-setting-info-spacer-cell" />
                    </tr>
                );
                return rows;
            }

            const userInfo = this.props.userInfo || {};

            // 内線番号を表示するかどうかを判定する関数(拠点/フロア管理者の場合、他拠点/フロアの内線グループの内線番号は表示しない)
            const canSeeExtensions = (group) => {
                // 会社管理者以上は全て表示
                if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.company)) return true;
                // 拠点管理者
                if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.department)) {
                    if (userInfo.floor_flg) {
                        // フロア有りの会社の場合は親拠点配下のみ表示
                        const myParentPrefix = (userInfo.department_prefix || '').slice(0, 2);
                        const groupParentPrefix = (group.department_prefix || '').slice(0, 2);
                        return myParentPrefix !== '' && myParentPrefix === groupParentPrefix;
                    }
                    // フロア無しの会社の場合は同一拠点のみ表示
                    return String(group.department_id) === String(userInfo.department_id);
                }
                // フロア管理者
                if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.floor)) {
                    return String(group.department_id) === String(userInfo.department_id);
                }
                // 処理はここまで実行されない想定だが、念のためreturn
                return true;
            };

            // 各内線グループに紐づく内線番号の個数の合計分の行数を確保する。
            // 権限制御により非表示の場合でも、1行分確保する。
            const totalRows = inboundGroups.reduce((total, group) => {
                const visible = canSeeExtensions(group);
                const extensionCount = visible ? Math.max(1, (group.extension_numbers?.length || 0)) : 1;
                return total + extensionCount;
            }, 0);

            inboundGroups.forEach((group, groupIndex) => {
                const canSee = canSeeExtensions(group);
                const extensionNumbers = canSee ? (group.extension_numbers || []) : [];
                const maxExtensionRows = Math.max(1, extensionNumbers.length);

                for (let j = 0; j < maxExtensionRows; j++) {
                    const extension = extensionNumbers[j];
                    const isFirstRow = rowIndex === 0;
                    const isFirstInGroup = j === 0;

                    rows.push(
                        <tr key={`external-${groupIndex}-${j}`}>
                            {isFirstRow && (
                                <td rowSpan={totalRows} className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border" style={{ padding: "2px 10px" }}>
                                    <div
                                        className="external-resource-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px margin-top-6-px"
                                    >
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faFax} size="lg" style={{ flexShrink: 0 }} />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis">{resourceMapData.externalNumbers[0].display_number}</div>
                                                {resourceMapData.externalNumbers[0].memo && (
                                                    <div
                                                        className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                        title={resourceMapData.externalNumbers[0].memo}
                                                    >({resourceMapData.externalNumbers[0].memo})</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            )}

                            {isFirstInGroup && (
                                <td
                                    rowSpan={maxExtensionRows}
                                    className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border"
                                    style={{ padding: "2px 10px" }}
                                >
                                    <div
                                        className={`external-group-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px ${canSee ? 'number-setting-info-clickable' : 'number-setting-info-is-disabled'} ${isFirstRow ? 'margin-top-6-px' : 'margin-top-0'}`}
                                        data-group-id={group.inbound_group_id}
                                        onClick={canSee ? ((event) => this.handleGroupClick(event, group.inbound_group_id, group.inbound_group_name)) : undefined}
                                        style={{ cursor: canSee ? 'pointer' : 'default' }}
                                    >
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faUsers} size="lg" style={{ flexShrink: 0 }} />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis" title={group.inbound_group_name}>{group.inbound_group_name}</div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            )}

                            <td className="number-setting-info-diagram-td font-weight-bold text-16-px" style={{ color: "#495057", position: "relative", padding: "2px 10px" }}>
                                {extension ? (
                                    <div
                                        className={`external-extension-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide number-setting-info-clickable number-setting-info-box-compact ${isFirstRow ? 'margin-top-6-px' : 'margin-top-0'}`}
                                        data-extension-id={extension.extension_number_id}
                                        data-group-id={group.inbound_group_id}
                                        onClick={(event) => this.handleExtensionClick(event, extension.extension_number_id, (extension.unique_extension_number || extension.extension_number))}
                                    >
                                        <div className="number-setting-info-row-center-gap-6 text-14-px"
                                            style={
                                                {
                                                    minWidth: 0 ,
                                                    gap: extension.type === 'softphone' ? '0.5px' : '6px'
                                                }
                                            }
                                        >
                                            <Favi
                                                className="sidebar-icon fa-fw"
                                                icon={this.getExtensionIcon(extension.type)}
                                                style={{ fontSize: extension.type === 'softphone' ? '11px' : '14px', flexShrink: 0 }}
                                            />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }} title={(extension.unique_extension_number || extension.extension_number) + (extension.memo ? ` (${extension.memo})` : "")}>
                                                <div className="truncate-ellipsis">
                                                    {(extension.unique_extension_number || extension.extension_number) + (extension.memo ? ` (${extension.memo})` : "")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                    );
                    rowIndex++;
                }
            });

            rows.push(
                <tr key={"spacer-row"}>
                    <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                    <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                    <td className="number-setting-info-spacer-cell" />
                </tr>
            );
            return rows;
        };

        // 内線グループ管理画面のボディ描画
        const renderBodyForInbound = () => {
            const rows = [];
            const externalNumbers = resourceMapData.externalNumbers || [];
            const extensionNumbers = resourceMapData.extensionNumbers || [];
            const totalRows = Math.max(externalNumbers.length, extensionNumbers.length, 1);

            for (let i = 0; i < totalRows; i++) {
                const isFirstRow = i === 0;

                rows.push(
                    <tr key={`inbound-group-${i}`}>
                        {isFirstRow && (
                            <td
                                rowSpan={totalRows}
                                className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border font-weight-bold text-14-px pos-relative number-setting-info-inbound-group-padding text-center v-top"
                            >
                                {externalNumbers && externalNumbers.length > 0 ? (
                                    <div>
                                        {externalNumbers.map((ext, idx) => (
                                            <div
                                                key={ext.external_number_id || idx}
                                                className={`inbound-external-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px number-setting-info-clickable ${idx === 0 ? 'margin-top-6-px' : 'margin-top-2-px'}`}
                                                data-external-id={ext.external_number_id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={(event) => this.handleExternalClick(event, ext.external_number_id, ext.display_number)}
                                            >
                                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                                    <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faFax} size="lg" style={{ flexShrink: 0 }} />
                                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                        <div className="truncate-ellipsis">{ext.display_number}</div>
                                                        {ext.memo && (
                                                            <div
                                                                className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                                title={ext.memo}
                                                            >({ext.memo})</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </td>
                        )}

                        {isFirstRow && (
                            <td
                                rowSpan={totalRows}
                                className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border number-setting-info-inbound-group-padding text-center v-top"
                            >
                                <div
                                    className="inbound-group-resource-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px margin-top-6-px"
                                    data-group-id={resourceMapData.inboundGroup.inbound_group_id}
                                >
                                    <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                        <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faUsers} size="lg" style={{ flexShrink: 0 }} />
                                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                            <div className="truncate-ellipsis" title={resourceMapData.inboundGroup.inbound_group_name}>{resourceMapData.inboundGroup.inbound_group_name}</div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        )}

                        {isFirstRow && (
                            <td rowSpan={totalRows} className="number-setting-info-diagram-td font-weight-bold text-16-px pos-relative number-setting-info-inbound-group-padding text-center v-top">
                                {extensionNumbers && extensionNumbers.length > 0 ? (
                                    <div>
                                        {extensionNumbers.map((ext, idx) => (
                                            <div
                                                key={ext.extension_number_id || idx}
                                                className={`inbound-extension-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide number-setting-info-clickable number-setting-info-box-compact ${idx === 0 ? 'margin-top-6-px' : 'margin-top-2-px'}`}
                                                data-extension-id={ext.extension_number_id}
                                                onClick={(event) => this.handleExtensionClick(event, ext.extension_number_id, (ext.unique_extension_number || ext.extension_number))}
                                            >
                                                <div
                                                    className="number-setting-info-row-center-gap-6 text-14-px"
                                                    style={
                                                        {
                                                            minWidth: 0,
                                                            gap: ext.type === 'softphone' ? '0.5px' : '6px'
                                                        }
                                                    }
                                                >
                                                <Favi
                                                    className="sidebar-icon fa-fw"
                                                    icon={this.getExtensionIcon(ext.type)}
                                                    style={{ fontSize: ext.type === 'softphone' ? '11px' : '14px', flexShrink: 0 }}
                                                />
                                                    <div style={{ minWidth: 0, overflow: 'hidden' }} title={(ext.unique_extension_number || ext.extension_number) + (ext.memo ? ` (${ext.memo})` : '')}>
                                                        <div className="truncate-ellipsis">
                                                            {(ext.unique_extension_number || ext.extension_number) + (ext.memo ? ` (${ext.memo})` : '')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </td>
                        )}
                    </tr>
                );
            }

            rows.push(
                <tr key={"spacer-row"}>
                    <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                    <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border" />
                    <td className="number-setting-info-spacer-cell" />
                </tr>
            );
            return rows;
        };

        // 内線番号管理画面のボディ描画
        const renderBodyForExtension = () => {
            const rows = [];
            let rowIndex = 0;
            const inboundGroups = resourceMapData.inboundGroups || [];

            if (inboundGroups.length === 0) {
                // 内線番号に設定されている内線グループが0個の場合
                rows.push(
                    <tr key={"no-groups"}>
                        <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border padding-10-px" />
                        <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border pos-relative padding-10-px" />
                        <td className="number-setting-info-diagram-td font-weight-bold text-14-px pos-relative padding-10-px text-center v-top">
                            <div className="extension-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50">
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <Favi
                                        className="sidebar-icon fa-fw"
                                        icon={this.getExtensionIcon(resourceMapData.extensionNumberType)}
                                        style={{ fontSize: resourceMapData.extensionNumberType === 'softphone' ? '12px' : '14px', flexShrink: 0 }}
                                    />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis">{resourceMapData.extensionNumber}</div>
                                        {resourceMapData.extensionName && (
                                            <div
                                                className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                title={resourceMapData.extensionName}
                                            >({resourceMapData.extensionName})</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                );
                return rows;
            }

            const totalRows = inboundGroups.reduce((total, g) => total + Math.max(1, (g.external_numbers?.length || 0)), 0);

            inboundGroups.forEach((group, groupIndex) => {
                const externalNumbers = group.external_numbers || [];
                const maxRows = Math.max(1, externalNumbers.length);

                for (let i = 0; i < maxRows; i++) {
                    const external = externalNumbers[i];
                    const isFirstRow = rowIndex === 0;
                    const isFirstInGroup = i === 0;

                    rows.push(
                        <tr key={`${groupIndex}-${i}`}>
                            <td
                                className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border padding-10-px text-center v-top"
                                style={{ cursor: external ? "pointer" : "default" }}
                                onClick={external ? (event) => this.handleExternalClick(event, external.external_number_id, external.display_number) : undefined}
                            >
                                {external ? (
                                    <div className={`external-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px ${external ? 'number-setting-info-clickable' : ''}`} data-group-id={group.inbound_group_id}>
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faFax} size="lg" style={{ flexShrink: 0 }} />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis">{external.display_number}</div>
                                                {external.memo && (
                                                    <div
                                                        className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                        title={external.memo}
                                                    >({external.memo})</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </td>

                            {isFirstInGroup && (
                                <td
                                    rowSpan={maxRows}
                                    className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border pos-relative padding-10-px text-center v-top"
                                >
                                    <div
                                        className="group-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px number-setting-info-clickable"
                                        data-group-id={group.inbound_group_id}
                                        onClick={(event) => this.handleGroupClick(event, group.inbound_group_id, group.inbound_group_name)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faUsers} size="lg" style={{ flexShrink: 0 }} />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis" title={group.inbound_group_name}>{group.inbound_group_name}</div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            )}

                            {isFirstRow && (
                                <td
                                    rowSpan={totalRows}
                                    className="number-setting-info-diagram-td font-weight-bold text-14-px pos-relative padding-10-px text-center v-top"
                                >
                                    <div className="extension-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50">
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <Favi
                                                className="sidebar-icon fa-fw"
                                                icon={this.getExtensionIcon(resourceMapData.extensionNumberType)}
                                                style={{ fontSize: resourceMapData.extensionNumberType === 'softphone' ? '12px' : '14px', flexShrink: 0 }}
                                            />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis">{resourceMapData.extensionNumber}</div>
                                                {resourceMapData.extensionName && (
                                                    <div
                                                        className="text-12-px margin-top-2-px op-90 truncate-ellipsis"
                                                        title={resourceMapData.extensionName}
                                                    >({resourceMapData.extensionName})</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            )}
                        </tr>
                    );
                    rowIndex++;
                }
            });

            return rows;
        };

        const bodyRows = sourceScreen === 'external'
            ? renderBodyForExternal()
            : sourceScreen === 'inbound'
                ? renderBodyForInbound()
                : renderBodyForExtension();

        return (
            <div className="number-setting-info-diagram-wrapper">
                <table className={`${tableClass} number-setting-info-diagram-table`}>
                    {renderHeader()}
                    <tbody>{bodyRows}</tbody>
                </table>
                <svg className={`${connectionLineClass} number-setting-info-diagram-connection-overlay`} xmlns="http://www.w3.org/2000/svg" />
            </div>
        );
    }

    // 内線種類に応じたFaviアイコンを取得する関数
    getExtensionIcon = (extensionType) => {
        switch (extensionType) {
            case 'hardphone':      // ソフトフォン・ハードフォン用内線番号
                return 'hardphone';
            case 'android':     // オムニフォン・オムニフォンモバイル用内線番号
                return 'android';
            case 'softphone': // オムニコンタクト・オムニフォン for PC用内線番号
                return 'softphone';
            default:
                return 'headphones'; // デフォルトはヘッドフォン
        }
    }

    // 既存のExternalNumber/board APIを使用（外線番号管理の一覧画面表示と同じAPI）
    fetchExternalNumberDetail = async (externalId) => {
        try {
            const reqBody = {
                start: 0,
                length: 10,
                search: { value: '' },
                columns: [],
                order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [
                    {
                        id: 'id',
                        value: externalId
                    }
                ]
            };

            const response = await this.props.ascAxios('post', 'ExternalNumber/board', reqBody);

            if (response.data && response.data.rows && Array.isArray(response.data.rows) && response.data.rows.length > 0) {
                const targetExternal = response.data.rows.find(row => row.id === parseInt(externalId));
                if (targetExternal) {
                    return targetExternal;
                } else {
                    console.warn(this.props.langText.Message.NumberSettingInfoDiagram.specifiedDisplayNumberDoesNotFound + externalId);
                    return null;
                }
            } else {
                console.warn(this.props.langText.Message.NumberSettingInfoDiagram.specifiedDisplayNumberInfoDoesNotFound);
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.specifiedDisplayNumberInfoGetError, error);
            throw error;
        }
    }

    // ExternalNumber.jsのgetBackNumbersメソッドと同等の処理
    getBackNumbers = async (cm61_id) => {
        try {
            if (!cm61_id) return [];

            const result = await this.props.ascAxios(
                "post",
                "ExternalNumber/getBackNumbers",
                { cm61_id }
            );

            const backNumbers = result.data.map(data => {
                return { label: data.back_number, value: data.back_number }
            });

            return backNumbers;
        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.getBackNumberError, error);
            return [];
        }
    }

    // 既存のInboundGroup/boardAPIを使用（内線グループ管理の一覧画面表示と同じAPI）
    fetchInboundGroupDetail = async (groupId) => {
        try {
            const reqBody = {
                start: 0,
                length: 10,
                search: { value: '' },
                columns: [],
                order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [
                    {
                        id: 'id',
                        value: [parseInt(groupId)]
                    }
                ]
            };

            const response = await this.props.ascAxios('post', 'InboundGroup/board', reqBody);

            if (response.data && response.data.rows && response.data.rows.length > 0) {
                return response.data.rows[0];
            } else {
                console.warn(this.props.langText.Message.NumberSettingInfoDiagram.specifiedInboundGroupDoesNotFound, groupId);
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.inboundGroupDetailGetError, error);
            throw error;
        }
    }

    // 既存のExtensionNumber/boardAPIを使用（内線番号管理の一覧画面表示と同じAPI）
    fetchExtensionNumberDetail = async (extensionId) => {
        try {
            const reqBody = {
                start: 0,
                length: 10,
                search: { value: '' },
                columns: [],
                order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [
                    {
                        id: 'id',
                        value: [parseInt(extensionId)]
                    }
                ]
            };

            const response = await this.props.ascAxios('post', 'ExtensionNumber/board', reqBody);

            if (response.data && response.data.rows && response.data.rows.length > 0) {
                return response.data.rows[0];
            } else {
                console.warn(this.props.langText.Message.NumberSettingInfoDiagram.specifiedExtensionNumberDoesNotFound, extensionId);
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.extensionNumberDetailGetError, error);
            throw error;
        }
    }

    // 外線番号クリック時のハンドラ
    handleExternalClick = async (event, externalId) => {
        if (event) {
            event.stopPropagation();
        }

        if (!externalId) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.missingExternalNumberId);
            return;
        }

        // ローディング状態を設定
        this.setState({
            externalNumberModalLoading: true,
            showExternalNumberModal: false,
            showInboundGroupModal: false,
            showExtensionNumberModal: false
        });

        try {
            const externalNumberData = await this.fetchExternalNumberDetail(externalId);

            if (externalNumberData) {
                await this.showExternalNumberModal(externalNumberData);
            } else {
                this.setState({
                    externalNumberModalLoading: false,
                    showExternalNumberModal: false
                });
            }
        } catch (error) {
            this.setState({
                externalNumberModalLoading: false,
                showExternalNumberModal: false
            });
            this.showErrorObjectMesssage(error);
        }
    };

    // 内線グループクリック時のハンドラ
    handleGroupClick = async (event, groupId) => {
        if (event) {
            event.stopPropagation();
        }

        if (!groupId) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.missingInboundGroupId);
            return;
        }

        // ローディング状態を設定
        this.setState({
            inboundGroupModalLoading: true,
            showInboundGroupModal: false,
            showExternalNumberModal: false,
            showExtensionNumberModal: false
        });

        try {
            const inboundGroupData = await this.fetchInboundGroupDetail(groupId);

            if (inboundGroupData) {
                await this.showInboundGroupModal(inboundGroupData);
            } else {
                this.setState({
                    inboundGroupModalLoading: false,
                    showInboundGroupModal: false
                });
            }
        } catch (error) {
            this.setState({
                inboundGroupModalLoading: false,
                showInboundGroupModal: false
            });
            this.showErrorObjectMesssage(error);
        }
    };

    // 内線番号クリック時のハンドラ
    handleExtensionClick = async (event, extensionId) => {
        if (event) {
            event.stopPropagation();
        }

        if (!extensionId) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.missingExtensionNumberId);
            return;
        }

        // ローディング状態を設定
        this.setState({
            extensionNumberModalLoading: true,
            showExtensionNumberModal: false,
            showInboundGroupModal: false,
            showExternalNumberModal: false
        });

        try {
            const extensionNumberData = await this.fetchExtensionNumberDetail(extensionId);

            if (extensionNumberData) {
                await this.showExtensionNumberModal(extensionNumberData);
            } else {
                this.setState({
                    extensionNumberModalLoading: false,
                    showExtensionNumberModal: false
                });
            }
        } catch (error) {
            this.setState({
                extensionNumberModalLoading: false,
                showExtensionNumberModal: false
            });
            this.showErrorObjectMesssage(error);
        }
    };

    // 外線番号モーダル表示メソッド
    showExternalNumberModal = async (externalNumberData) => {
        try {
            // state設定(静的)
            let setData = {
                inboundTypeSelect: this.getSelectOption('external_inbound_group_type', this.props.langText.SelectOption),
                voice_log_expire_external_number_select: this.getSelectOption("voice_log_expire_external_number", this.props.langText.SelectOption),
                UnconnectedActionSelect: this.getSelectOption('unconnected_action', this.props.langText.SelectOption),
                inboundGroupSelect: [],
                inboundActionSelect: this.getSelectOption('inbound_action', this.props.langText.SelectOption),
                callOverActionSelect: this.getSelectOption('call_over_action', this.props.langText.SelectOption),
                callOverActionControlSelect: this.getSelectOption('call_over_action_control', this.props.langText.SelectOption),
                timeoutPlaybackSelect: this.getSelectOption('timeout_playback', this.props.langText.SelectOption),
                DialRestrictionSelect : this.getSelectOption('dial_restriction', this.props.langText.SelectOption),
                anonymousActionSelect: this.getSelectOption('anonymous_action', this.props.langText.SelectOption)
            };

            // state設定(DB取得値)
            if (externalNumberData) {
                // 基本設定
                setData.external_id = externalNumberData.id || "";
                setData.gateway_id = externalNumberData.cm91_id
                    ? externalNumberData.cm91_id
                    : ''
                ;
                setData.gateway_name = externalNumberData.cm91_gateway_infos?.gw_name || '';
                setData.company_id = externalNumberData.cm12_companies?.id || "";
                setData.company_name = externalNumberData.cm12_companies?.company_name || "";

                // GW名リスト
                try {
                    const gwRes = await this.props.ascAxios('post', 'Common/companyBygatewaySelect', { company_id: setData.company_id });
                    setData.gatewaySelect = gwRes.data || [];
                } catch (e) {
                    setData.gatewaySelect = [];
                }

                // 回線種別リスト
                try {
                    const ltRes = await this.props.ascAxios('post', 'Common/getLineTypes', { parameterId: 'LINE_TYPE' });
                    setData.lineTypeSelect = ltRes.data || [];
                } catch (e) {
                    setData.lineTypeSelect = [];
                }
                setData.display_number = externalNumberData.display_number || "";
                setData.back_number = await this.getBackNumbers(externalNumberData.id);
                setData.channel_number = externalNumberData.channel_number !== "0" ? externalNumberData.channel_number : "";
                setData.memo = externalNumberData.memo || "";
                setData.description = externalNumberData.description || "";

                // 待ち呼関連
                setData.wait_number = (externalNumberData.wait_number && externalNumberData.wait_number >= 0) || externalNumberData.wait_number === 0 ? externalNumberData.wait_number : "";
                setData.waitCallTimeoutSeconds = externalNumberData.wait_call_timeout_sec || "";

                // 拠点・フロア関連の設定
                const departmentInfo = Array.isArray(externalNumberData.ct62_assgin_external_department_infos) &&
                    externalNumberData.ct62_assgin_external_department_infos.length > 0
                    ? externalNumberData.ct62_assgin_external_department_infos[0]
                    : null;
                const departmentInfos = externalNumberData.ct62_assgin_external_department_infos
                    ? externalNumberData.ct62_assgin_external_department_infos.map(row => {
                        return {
                            id: row.id,
                            cm61_id: row.cm61_id,
                            cm13_id: row.cm13_departments.id,
                            department_id: row.cm13_departments.id,
                            parent_department_id: row.cm13_departments.parent_department_id,
                            floor_id: row.cm13_departments.floor_id,
                            prefix: row.prefix,
                            full_prefix: row.full_prefix,
                            department_name: row.cm13_departments.department_name,
                            floor_name: row.cm13_departments.floor_name,
                            prefix_duplicationflag: false
                        };
                    })
                    : []
                ;
                setData.departmentInfos = departmentInfos;
                setData.department_id = departmentInfo?.cm13_departments?.id || "";
                setData.department_name = departmentInfo?.cm13_departments?.department_name || "";
                setData.floor_flg = externalNumberData.cm12_companies?.floor_flg === "Y";
                setData.prefix = externalNumberData.sequence_number || "";

                // 内線グループ関連の設定
                setData.inbound_type = externalNumberData.inbound_group_type || "";
                setData.inboundTypeSelect = this.getSelectOption('external_inbound_group_type', this.props.langText.SelectOption);
                setData.inbound_group_id = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.id)
                    : [];
                setData.inbound_group_name = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.inbound_group_name)
                    : [];
                // 内線グループごとの内線番号個数
                try {
                    const res = await this.ascAxios("post", "/ExternalNumber/inboundGroupInfoSelect", {
                        department_id: departmentInfos.map(row => row.cm13_id),
                        inbound_type: externalNumberData.inbound_group_type
                    })
                    setData.inbound_group_cm51ids = res.data.cm28_extensions_ids;
                } catch (e) {
                    setData.inbound_group_cm51ids = [];
                }

                // 音声関連の設定
                setData.sound_id = externalNumberData.cm35_id !== "0" ? externalNumberData.cm35_id : "";
                setData.call_over_sound_id = externalNumberData.cm35_id_call_over_sound || "";
                setData.before_dial_sound_id = externalNumberData.cm35_id_before_dial || "";
                setData.cm35_id_timeout_sound = externalNumberData.cm35_id_timeout_sound || "";
                setData.unconnected_sound_id = externalNumberData.cm35_id_unconnected_sound || "";
                setData.anonymous_sound_id = externalNumberData.cm35_id_anonymous_sound || "";
                setData.wait_call_timeout_sound_id = externalNumberData.cm35_id_wait_call_timeout_sound || "";
                setData.waitCallTimeoutSoundId = externalNumberData.cm35_id_wait_call_timeout_sound || "";

                // 営業カレンダー情報の設定
                setData.business_calendar_id = externalNumberData.cm31_business_calendar_infos?.id || "";
                setData.business_calendar_name = externalNumberData.cm31_business_calendar_infos?.business_calendar_name || "";

                // 通話品質分析設定
                setData.callAnalysisFlag =
                    !!(
                        externalNumberData.cc81_setting_call_quality_external_number &&
                        externalNumberData.cc81_setting_call_quality_external_number.parameter_value === 1
                    )
                ;

                // 応対品質分析設定
                setData.callEvalFlag =
                    !!(
                        externalNumberData.cc82_setting_call_eval_external_number &&
                        externalNumberData.cc82_setting_call_eval_external_number.parameter_value === 1
                    )
                ;

                // クイックモード設定
                setData.fastOptionFlag =
                    !!(
                        externalNumberData.cc81_setting_call_quality_external_number &&
                        externalNumberData.cc81_setting_call_quality_external_number.fast_option ==="Y"
                    )
                ;

                // スケジュール情報の設定
                setData.schedule_extension_group_infos = externalNumberData.ct30_assign_external_number_schedule_extension_group_infos || [];
                setData.schedule_wait_call_infos = externalNumberData.ct32_assign_external_number_schedule_wait_call_infos || [];
                setData.schedule_over_wait_call_infos = externalNumberData.ct33_assign_external_number_schedule_over_wait_call_infos || [];
                setData.schedule_dispensing_infos = externalNumberData.ct31_assign_external_number_schedule_dispensing_infos || [];

                // フラグの設定
                setData.acd_flag = externalNumberData.acd_flag === "Y";
                setData.abj_flag = externalNumberData.abj_flag === "Y";
                setData.before_dial_flag = externalNumberData.before_dial_flag === "Y";
                setData.waitCallTimeoutUseFlag = externalNumberData.wait_call_timeout_flag === "Y";
                setData.call_history_delete_flag = externalNumberData.call_history_delete_flag === "Y";
                setData.voice_log_delete_flag = externalNumberData.voice_log_delete_flag === "Y";

                // その他の設定
                setData.timeout_sec = externalNumberData.timeout_sec || "";
                setData.call_over_action = externalNumberData.call_over_action || "";
                setData.call_over_action_control = externalNumberData.call_over_action_control || "";
                setData.timeout_action = externalNumberData.timeout_action
                    ? externalNumberData.timeout_action
                    : externalNumberData.timeout_playback_flag === "Y"
                        ? "playback"
                        : "unset"
                ;
                setData.unconnected_action = externalNumberData.unconnected_action || "";
                setData.anonymous_action = externalNumberData.anonymous_action || 'unset';
                setData.line_type = externalNumberData.line_type || "";
                setData.voice_log_expire_external_number =
                    (externalNumberData.voice_log_expire_external_number || externalNumberData.voice_log_expire_external_number === 0)
                        ? externalNumberData.voice_log_expire_external_number
                        : -1;
                setData.wait_call_timeout_sec = externalNumberData.wait_call_timeout_sec || "";
                setData.call_results = externalNumberData.ct602_assign_call_results
                    ? externalNumberData.ct602_assign_call_results.map(row => row.cm64_id)
                    : [];
                setData.omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color
                    ? externalNumberData.omni_phone_back_ground_color
                    : GlobalConst.omni_phone_back_ground_color;
                setData.omni_phone_font_color = externalNumberData.omni_phone_font_color
                    ? externalNumberData.omni_phone_font_color
                    : GlobalConst.omni_phone_font_color;
                setData.before_omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color
                    ? externalNumberData.omni_phone_back_ground_color
                    : '';
                setData.before_omni_phone_font_color = externalNumberData.omni_phone_font_color
                    ? externalNumberData.omni_phone_font_color
                    : '';
                setData.dial_restriction_flag = externalNumberData.dial_restriction_flag
                    ? externalNumberData.dial_restriction_flag
                    : "N"
                ;
                setData.listAppointInboundFlag = externalNumberData.cm76_autocall_list && externalNumberData.cm76_autocall_list.id;
                setData.list_out_action = externalNumberData.list_out_action
                    ? externalNumberData.list_out_action
                    : ''
                ;
                setData.list_name = externalNumberData.cm76_autocall_list
                    ? externalNumberData.cm76_autocall_list.list_name
                    : ''
                ;
                setData.list_sound_id = externalNumberData.cm35_id_list_sound
                    ? externalNumberData.cm35_id_list_sound
                    : ''
                ;
                setData.dispensing_time = externalNumberData.dispensing_time || "";
                setData.dispensing_count = externalNumberData.dispensing_count || "";
                setData.dispensing_flag = !!(externalNumberData.dispensing_time || externalNumberData.dispensing_count);
            }

            if (externalNumberData.cm12_companies.id) {
                try {
                    let langtext_update = this.props.langText.Body.VoiceListUpdate;
                    let langtext_create = this.props.langText.Body.VoiceListCreate;
                    // 会社ごとに紐づく情報を取得
                    const resCompanyInfo =
                        await this.ascAxios('post', "/ExternalNumber/getItemsOnCompanySelected", {
                            company_id: externalNumberData.cm12_companies.id,
                            langtext_update,
                            langtext_create
                        });
                    setData.waitNumberSoundSelect =  resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listOutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.beforeDialSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listSelect = resCompanyInfo.data.cm76_autocall_list;
                    setData.callOverSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.UnconnectedSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.timeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.anonymousSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.waitCallTimeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.callResultsSelect = resCompanyInfo.data.cm64_call_results;
                    setData.call_history_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies 
                        && resCompanyInfo.data.cm12_companies.call_history_delete_flag && resCompanyInfo.data.cm12_companies.call_history_delete_flag === "Y"
                        ? true
                        : false;
                    setData.voice_log_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies
                        && resCompanyInfo.data.cm12_companies.voice_log_delete_flag && resCompanyInfo.data.cm12_companies.voice_log_delete_flag === "Y"
                        ? true
                        : false;

                    // 会社管理の追加オプション情報取得
                    const resCompanyControlParam =
                        await this.ascAxios('post', "/ExternalNumber/getCompanyControlParameter", {company_id: externalNumberData.cm12_companies.id});
                    setData.addingOptionCallAnalysis =
                        resCompanyControlParam.data.paramValueCallAnalysis === "N" ? false : true;
                    setData.addingFastOption =
                        resCompanyControlParam.data.paramValueFastOption === "ascend_whisper" ? true : false;
                    setData.addingOptionACD =
                        resCompanyControlParam.data.paramValueACD === "N" ? false : true;
                    setData.addingOptionCallEval =
                        resCompanyControlParam.data.paramValueCallEval === "N" ? false : true;
                    setData.addingOptionCallResult =
                        resCompanyControlParam.data.paramValueCallResult === "N" ? false : true;
                } catch (e) {
                    // 処理は継続
                }
            }

            // 外線番号情報モーダル表示(閲覧モード)
            setData.showExternalNumberModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            // モーダル状態の更新
            this.setState({
                externalNumberModalData: setData,
                showExternalNumberModal: true,
                externalNumberModalLoading: false,
                showInboundGroupModal: false,
                showExtensionNumberModal: false,
                inboundGroupModalLoading: false,
                extensionNumberModalLoading: false
            });

        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.externalNumberModalShowError, error);
            this.setState({
                showExternalNumberModal: false,
                externalNumberModalLoading: false
            });
        }
    }

    // 内線グループモーダル表示メソッド
    showInboundGroupModal = async (inboundGroupData) => {
        try {
            // state設定(静的)
            let setData = {
                inboundGroupTypeSelect: this.getSelectOption(
                    'inbound_group_type',
                    this.props.langText.SelectOption
                )
            };

            // state設定(DB取得値)
            if (inboundGroupData) {
                setData.inbound_group_info_id = inboundGroupData.id || "";
                setData.company_id = inboundGroupData.cm13_departments?.cm12_companies?.id || "";
                setData.company_name = inboundGroupData.cm13_departments?.cm12_companies?.company_name || "";
                setData.floor_flg = inboundGroupData.cm13_departments?.cm12_companies?.floor_flg === "Y";
                if (setData.floor_flg) {
                    setData.floor_id = inboundGroupData.cm13_departments?.id || "";
                    setData.floor_name = inboundGroupData.cm13_departments?.floor_name || "";
                }
                setData.department_id = inboundGroupData.cm13_departments?.id || "";
                setData.department_name = inboundGroupData.cm13_departments?.department_name || "";
                setData.group_name = inboundGroupData.inbound_group_name || "";
                setData.group_type = inboundGroupData.inbound_type || setData.group_type;
                setData.group_type_org = inboundGroupData.inbound_type || setData.group_type;

                setData.extension_number = inboundGroupData.cm51_extension_numbers
                    ? inboundGroupData.cm51_extension_numbers
                        .sort((a, b) => {
                            const aNumber = a.unique_extension_number || a.extension_number || '';
                            const bNumber = b.unique_extension_number || b.extension_number || '';
                            return aNumber.localeCompare(bNumber);
                        })
                        .map(row => row.id)
                    : [];

                setData.extensionNumberSelect = inboundGroupData.cm51_extension_numbers
                    ? inboundGroupData.cm51_extension_numbers
                        .sort((a, b) => {
                            const aNumber = a.unique_extension_number || a.extension_number || '';
                            const bNumber = b.unique_extension_number || b.extension_number || '';
                            return aNumber.localeCompare(bNumber);
                        })
                        .map(row => {
                            const numberStr = row.unique_extension_number || row.extension_number || '';
                            // 末尾の英字を除去
                            const normalizedNumber = numberStr.replace(/[a-zA-Z]+$/, '');
                            const nameStr = `(${row.memo || ''})`;
                            const labelText = `${normalizedNumber} ${nameStr}`;
                            const labeldata = React.createElement('div', null, [
                                labelText,
                                React.createElement(Favi, {
                                    key: 'icon',
                                    className: 'pull-right fa-fw fa-icon fa-icon-static',
                                    icon: row.type
                                })
                            ]);

                            return {
                                value: row.id,
                                label: labeldata,
                                search: `${normalizedNumber} ${row.memo || ''}`.trim(),
                                extension_number: normalizedNumber,
                                extension_name: row.memo || '',
                                type: row.type || 'normal'
                            };
                        })
                    : [];
            }

            // 内線グループ情報モーダルを表示(閲覧モード)
            setData.showInboundGroupModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            // モーダル状態の更新
            this.setState({
                inboundGroupModalData: setData,
                showInboundGroupModal: true,
                inboundGroupModalLoading: false,
                showExternalNumberModal: false,
                showExtensionNumberModal: false,
                externalNumberModalLoading: false,
                extensionNumberModalLoading: false
            });

        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.inboundGroupModalShowError, error);
            this.setState({
                showInboundGroupModal: false,
                inboundGroupModalLoading: false
            });
        }
    }

    // 内線番号モーダル表示メソッド
    showExtensionNumberModal = async (extensionNumberData) => {
        try {
            // state設定(静的)
            let setData = {
                externalSettingTypeRadio: this.getSelectOption('external_setting_type', this.props.langText.SelectOption),
                inboundTypeRadio:   this.getSelectOption('inbound_type', this.props.langText.SelectOption),
                inboundTypeSelect: this.getSelectOption('inbound_all_type', this.props.langText.SelectOption),
                monitoringFlgRadio: this.getSelectOption('monitoring_flg', this.props.langText.SelectOption),
                softphoneCodecTypeSelect: this.getSelectOption('softphone_codec_type', this.props.langText.SelectOption)
            };

            // state設定(DB取得値)
            if (extensionNumberData) {
                setData.extension_number_id = extensionNumberData.id || "";
                setData.company_id = extensionNumberData.cm13_departments?.cm12_companies?.id || "";
                setData.company_name = extensionNumberData.cm13_departments?.cm12_companies?.company_name || "";
                setData.department_id = extensionNumberData.cm13_departments?.id || "";
                setData.department_name = extensionNumberData.cm13_departments?.department_name || "";
                setData.floor_flg = extensionNumberData.cm13_departments?.cm12_companies?.floor_flg === "Y";
                if (setData.floor_flg) {
                    setData.floor_id = extensionNumberData.cm13_departments?.id || "";
                    setData.floor_name = extensionNumberData.cm13_departments?.floor_name || "";
                }
                setData.extension_number = extensionNumberData.extension_number || "";
                setData.memo = extensionNumberData.memo || "";
                setData.type = extensionNumberData.type || "";
                setData.inbound_type = extensionNumberData.inbound_type || extensionNumberData.type || "";
                setData.monitoring_flg = extensionNumberData.monitoring_flg || "";
                setData.omni_phone_version = extensionNumberData.omni_phone_version || "";
                // デフォルト発信番号
                if (extensionNumberData.cm61_external_numbers) {
                    setData.external_number = extensionNumberData.cm61_external_numbers.id || "";
                    setData.external_number_name = extensionNumberData.cm61_external_numbers.display_number || "";
                }
                // 内線種類
                const typeOpt = (setData.extensionTypeSelect || []).find(o => o.value === setData.inbound_type);
                setData.inboung_type_name = typeOpt ? typeOpt.label : "";
                // 内線グループ
                const cm28Groups = extensionNumberData.ct28_assign_inbound_group_infos && extensionNumberData.ct28_assign_inbound_group_infos.cm28_inbound_group_infos
                    ? extensionNumberData.ct28_assign_inbound_group_infos.cm28_inbound_group_infos
                    : [];
                if (Array.isArray(cm28Groups) && cm28Groups.length > 0) {
                    const sortedGroups = [...cm28Groups].sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
                    setData.inbound_group_id = sortedGroups.map(row => row.id);
                    setData.inboundGroupSelect = sortedGroups.map(row => ({ value: row.id, label: row.inbound_group_name }));
                }
            }

            // 内線番号情報モーダルを表示(閲覧モード)
            setData.showExtensionNumberModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            // モーダル状態の更新
            this.setState({
                extensionNumberModalData: setData,
                showExtensionNumberModal: true,
                extensionNumberModalLoading: false,
                showExternalNumberModal: false,
                showInboundGroupModal: false,
                externalNumberModalLoading: false,
                inboundGroupModalLoading: false
            });

        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.extensionNumberModalShowError, error);
            this.setState({
                showExtensionNumberModal: false,
                extensionNumberModalLoading: false
            });
        }
    }

    // ExtensionNumberModal用の関数群
    getinboundGroupName = () => {
        const child = this.state.extensionNumberModalData || {};
        const parent = this.props.state || {};

        const ids = Array.isArray(child.inbound_group_id) && child.inbound_group_id.length > 0
            ? child.inbound_group_id
            : (parent.inbound_group_id || []);

        const options = Array.isArray(child.inboundGroupSelect) && child.inboundGroupSelect.length > 0
            ? child.inboundGroupSelect
            : (parent.inboundGroupSelect || []);

        if (!ids || ids.length === 0 || !options || options.length === 0) return "";

        const idSet = new Set(ids.map(v => String(v)));
        const labels = options
            .filter(opt => idSet.has(String(opt.value)))
            .sort((a, b) => Number(a.value || 0) - Number(b.value || 0))
            .map(opt => opt.label);
        return labels.join(",");
    }

    getSoundName = (cm35_id) => {
        let {listOutSoundSelect} = this.state.externalNumberModalData;
        let returnData = '';
        listOutSoundSelect.forEach((row, key)=>{
            if(row.value == cm35_id){
                returnData = row.label
            }
        })
        return returnData;
    }

    // CSV出力処理
    createCsvData = async () => {
        try {
            this.setState({ csvLoading: true });

            const { state, langText } = this.props;
            const { resourceMapData } = state;

            // 呼び出し元画面の情報を取得
            const sourceScreen = this.props.sourceScreen || this.state.sourceScreen;

            // データの種類に応じてCSV形式を決定
            let csvData = [];
            let csvHeader = [];

            // 共通のCSVヘッダー（5列）
            csvHeader = [
                { label: langText.Body.NumberSettingInfoDiagram.csvHeaderDisplayNumber, key: 'externalNumber' },
                { label: langText.Body.NumberSettingInfoDiagram.csvHeaderDisplayNumberMemo, key: 'externalNumberName' },
                { label: langText.Body.NumberSettingInfoDiagram.csvHeaderInboundGroup, key: 'inboundGroup' },
                { label: langText.Body.NumberSettingInfoDiagram.csvHeaderExtensionNumber, key: 'extensionNumber' },
                { label: langText.Body.NumberSettingInfoDiagram.csvHeaderExtensionNumberMemo, key: 'extensionName' }
            ];

            // 呼び出し元画面に基づいてデータを作成
            if (sourceScreen === 'extension') {

                // 内線番号の情報を取得
                // 末尾にaが入る場合があるので、内線番号から英字をトリミング
                const extensionNumber = (resourceMapData.extensionNumber || '').replace(/[a-zA-Z]+$/, '');
                const extensionName = resourceMapData.extensionName || '';

                // 関連する外線番号と内線グループの情報を取得
                if (resourceMapData.inboundGroups && resourceMapData.inboundGroups.length > 0) {
                    for (const inboundGroup of resourceMapData.inboundGroups) {
                        // 内線グループに設定されている外線番号を取得
                        const groupExternalNumbers = inboundGroup.external_numbers || [];
                        if (groupExternalNumbers.length > 0) {
                            // 外線番号ごとに1行ずつ出力
                            for (const externalNumberData of groupExternalNumbers) {
                                csvData.push({
                                    externalNumber: externalNumberData.display_number || '',
                                    externalNumberName: externalNumberData.memo || '',
                                    inboundGroup: inboundGroup.inbound_group_name || '',
                                    extensionNumber: extensionNumber,
                                    extensionName: extensionName
                                });
                            }
                        } else {
                            // 外線番号が設定されていない場合でも1行出力
                            csvData.push({
                                externalNumber: '',
                                externalNumberName: '',
                                inboundGroup: inboundGroup.inbound_group_name || '',
                                extensionNumber: extensionNumber,
                                extensionName: extensionName
                            });
                        }
                    }
                } else {
                    // 内線グループがない場合
                    const externalNumbers = resourceMapData.externalNumbers || [];
                    if (externalNumbers.length > 0) {
                        // 外線番号ごとに1行ずつ出力
                        for (const externalNumberData of externalNumbers) {
                            csvData.push({
                                externalNumber: externalNumberData.display_number || '',
                                externalNumberName: externalNumberData.memo || '',
                                inboundGroup: '',
                                extensionNumber: extensionNumber,
                                extensionName: extensionName
                            });
                        }
                    } else {
                        // 外線番号も内線グループもない場合
                        csvData.push({
                            externalNumber: '',
                            externalNumberName: '',
                            inboundGroup: '',
                            extensionNumber: extensionNumber,
                            extensionName: extensionName
                        });
                    }
                }
            } else if (sourceScreen === 'external') {
                // 外線番号情報を取得（resourceMapDataから）
                const externalNumber = resourceMapData.externalNumbers && resourceMapData.externalNumbers.length > 0
                    ? resourceMapData.externalNumbers[0]
                    : null;

                const inboundGroups = resourceMapData.inboundGroups || [];

                if (inboundGroups.length > 0) {
                    // 内線グループごとにデータを作成
                    for (const inboundGroup of inboundGroups) {
                        // 権限制御: 表示対象外のグループは内線番号をCSVにも出さない
                        // (画面同様、拠点/フロア管理者の場合、他拠点/フロアの内線グループの内線番号は表示しない)
                        const canSee = (() => {
                            const userInfo = this.props.userInfo || {};
                            // 会社管理者以上は全て表示
                            if (this.props.getScopeGreaterEqual && this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.company)) return true;
                            // 拠点管理者
                            if (this.props.getScopeGreaterEqual && this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.department)) {
                                if (userInfo.floor_flg) {
                                    // フロア有りの会社の場合は親拠点配下のみ表示
                                    const myParentPrefix = (userInfo.department_prefix || '').slice(0, 2);
                                    const groupParentPrefix = (inboundGroup.department_prefix || '').slice(0, 2);
                                    return myParentPrefix !== '' && myParentPrefix === groupParentPrefix;
                                }
                                // フロア無しは同一拠点のみ表示
                                return String(inboundGroup.department_id) === String(userInfo.department_id);
                            }
                            // フロア管理者
                            if (this.props.getScopeGreaterEqual && this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.floor)) {
                                return String(inboundGroup.department_id) === String(userInfo.department_id);
                            }

                            // 処理はここまで来ない想定だが、念のためreturn
                            return true;
                        })();

                        const extensionNumbers = canSee ? (inboundGroup.extension_numbers || []) : [];

                        if (extensionNumbers.length > 0) {
                            // 内線番号がある場合
                            for (const extensionNumber of extensionNumbers) {
                                csvData.push({
                                    externalNumber: externalNumber ? externalNumber.display_number || '' : '',
                                    externalNumberName: externalNumber ? externalNumber.memo || '' : '',
                                    inboundGroup: inboundGroup.inbound_group_name || '',
                                    extensionNumber: (extensionNumber.extension_number || extensionNumber.unique_extension_number || '').replace(/[a-zA-Z]+$/, ''),
                                    extensionName: extensionNumber.memo || ''
                                });
                            }
                        } else {
                            // 内線番号がない場合でも1行出力
                            // 権限外グループの場合は空欄ではなくプレースホルダを表示
                            csvData.push({
                                externalNumber: externalNumber ? externalNumber.display_number || '' : '',
                                externalNumberName: externalNumber ? externalNumber.memo || '' : '',
                                inboundGroup: inboundGroup.inbound_group_name || '',
                                extensionNumber: canSee ? '' : langText.Body.NumberSettingInfoDiagram.noReferencePlaceholder,
                                extensionName: canSee ? '' : langText.Body.NumberSettingInfoDiagram.noReferencePlaceholder
                            });
                        }
                    }
                } else {
                    // 紐づく内線グループが0件でも出力
                    csvData.push({
                        externalNumber: externalNumber ? externalNumber.display_number || '' : '',
                        externalNumberName: externalNumber ? externalNumber.memo || '' : '',
                        inboundGroup: '',
                        extensionNumber: '',
                        extensionName: ''
                    });
                }
            } else if (sourceScreen === 'inbound') {
                // 内線グループ管理形式のCSVデータ作成

                const inboundGroupObj = resourceMapData.inboundGroup || {};
                const groupName = inboundGroupObj.inbound_group_name || '';
                const externalNumbers = resourceMapData.externalNumbers || [];
                const extensionNumbers = resourceMapData.extensionNumbers || [];

                if (externalNumbers.length > 0) {
                    for (const external of externalNumbers) {
                        if (extensionNumbers.length > 0) {
                            for (const extensionNumber of extensionNumbers) {
                                csvData.push({
                                    externalNumber: external.display_number || '',
                                    externalNumberName: external.memo || '',
                                    inboundGroup: groupName,
                                    extensionNumber: (extensionNumber.extension_number || extensionNumber.unique_extension_number || '').replace(/[a-zA-Z]+$/, ''),
                                    extensionName: extensionNumber.memo || ''
                                });
                            }
                        } else {
                            // 内線番号が無い場合でも外線ごとに1行
                            csvData.push({
                                externalNumber: external.display_number || '',
                                externalNumberName: external.memo || '',
                                inboundGroup: groupName,
                                extensionNumber: '',
                                extensionName: ''
                            });
                        }
                    }
                } else {
                    // 外線番号が無い場合は外線欄空で内線を出力
                    if (extensionNumbers.length > 0) {
                        for (const extensionNumber of extensionNumbers) {
                            csvData.push({
                                externalNumber: '',
                                externalNumberName: '',
                                inboundGroup: groupName,
                                extensionNumber: (extensionNumber.extension_number || extensionNumber.unique_extension_number || '').replace(/[a-zA-Z]+$/, ''),
                                extensionName: extensionNumber.memo || ''
                            });
                        }
                    } else {
                        csvData.push({
                            externalNumber: '',
                            externalNumberName: '',
                            inboundGroup: groupName,
                            extensionNumber: '',
                            extensionName: ''
                        });
                    }
                }
            }

            if (csvData.length === 0) {
                console.error(langText.Message.NumberSettingInfoDiagram.csvEmptyResourceMapDataError);
                alert(langText.Message.NumberSettingInfoDiagram.csvNoDataToOutputAlert);
                this.setState({ csvLoading: false });
                return;
            }

            // CSVダウンロード実行
            this.setState({
                csvData,
                csvHeader,
                csvLoading: false
            }, () => {
                // stateの更新完了後にCSVダウンロードを実行
                // (確実にstate更新後に処理を行うため、100ミリ秒遅延)
                setTimeout(() => {
                    this.csvLink.current.link.click();
                }, 100);
            });

        } catch (error) {
            console.error(this.props.langText.Message.NumberSettingInfoDiagram.csvOutputError, error);
            this.setState({ csvLoading: false });
            alert(this.props.langText.Message.NumberSettingInfoDiagram.csvOutputFailedAlert);
        }
    }

    // CSVファイル名生成
    getCsvFileName = () => {
        const { state } = this.props;
        const { resourceMapData } = state;

        // 会社名を取得
        let companyName = '';

        if (resourceMapData && resourceMapData.companyName) {
            // resourceMapDataに設定された会社名を使用
            companyName = resourceMapData.companyName;
        } else {
            companyName = this.props.userInfo.company_name;
        }

        const nowTime = moment().utc().add(9, "h").format("YYYYMMDDHHmmss");
        return `${companyName}_${this.props.langText.Body.NumberSettingInfoDiagram.csvFileNameFixedWord}_${nowTime}.csv`;
    }

    // モーダルクローズメソッド群
    closeExternalNumberModal = () => {
        this.setState({
            showExternalNumberModal: false,
            externalNumberModalLoading: false,
            externalNumberModalData: null
        });
    }

    closeInboundGroupModal = () => {
        this.setState({
            showInboundGroupModal: false,
            inboundGroupModalLoading: false,
            inboundGroupModalData: null
        });
    }

    closeExtensionNumberModal = () => {
        this.setState({
            showExtensionNumberModal: false,
            extensionNumberModalLoading: false,
            extensionNumberModalData: null
        });
    }

    render() {
        const {
            state,
            langText,
            tableClass,
            connectionLineClass
        } = this.props;

        const { showResourceMap, resourceMapLoading, resourceMapData } = state;

        // AscModalで使用するためのstateオブジェクトを構築
        const modalState = {
            show: showResourceMap,
            modalType: "read", // 閲覧モーダルとして表示
            showConfirm: false
        };

        // AscModalのpropSetStateをラップして、showResourceMapも同期更新する
        const wrappedPropSetState = (newState) => {
            // showプロパティが変更された場合は、showResourceMapも一緒に更新
            if (newState && typeof newState.show !== 'undefined') {
                // モーダルが閉じられる場合
                if (!newState.show) {
                    // 子モーダルも一緒に閉じる
                    this.setState({
                        showExternalNumberModal: false,
                        showInboundGroupModal: false,
                        showExtensionNumberModal: false,
                        externalNumberModalLoading: false,
                        inboundGroupModalLoading: false,
                        extensionNumberModalLoading: false,
                        externalNumberModalData: null,
                        inboundGroupModalData: null,
                        extensionNumberModalData: null
                    });
                    this.closeNumberSettingInfoModal();
                }
            }
        };

        return (
            <div>
                <AscModal
                    state={modalState}
                    propSetState={wrappedPropSetState}
                    langText={{
                        ...langText,
                        Body: {
                            ...(langText?.Body || {}),
                            ReadTitle: langText.Body.NumberSettingInfoDiagram.title,
                            Cancel: langText.Body.Close
                        }
                    }}
                    dialogClassName="number-setting-info-diagram-modal non-scroll-bar"
                >
                    <Panel bsStyle="info">
                        <Panel.Heading>
                            <Panel.Title style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                                <span style={{ fontWeight: "bold" }}>{langText.Body.NumberSettingInfoDiagram.titleInside}</span>
                                <Button
                                    className="table-button"
                                    bsStyle="default"
                                    bsSize="sm"
                                    onClick={this.createCsvData}
                                    disabled={this.state.csvLoading}
                                >
                                    {
                                        this.state.csvLoading
                                        ? langText.Body.NumberSettingInfoDiagram.csvLoading
                                        : langText.Body.NumberSettingInfoDiagram.csvButton
                                    }
                                </Button>
                            </Panel.Title>
                        </Panel.Heading>

                        <Panel.Body style={{ position: "relative" }}>
                            <Row>
                                <Col md={12}>
                                    {resourceMapLoading ? (
                                        <div className="text-center">
                                            <p>{langText.Table.LoadingText}</p>
                                        </div>
                                    ) : resourceMapData ? (
                                        <div>
                                            {/* テーブル内容の描画 */}
                                            {this.renderTableContentCommon({ resourceMapData, tableClass, connectionLineClass })}
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <p>{langText.Body.NumberSettingInfoDiagram.dataReadError}</p>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                </AscModal>

                {/* 外線番号詳細モーダル */}
                {this.state.showExternalNumberModal && (
                    <ExternalNumberModal
                        state={this.state.externalNumberModalData}
                        propSetState={(newState) => {
                            // モーダルが閉じられる場合
                            if (newState && newState.show === false) {
                                this.closeExternalNumberModal();
                            } else {
                                // データの更新があった場合は子モーダルのデータを更新
                                this.setState({
                                    externalNumberModalData: { ...this.state.externalNumberModalData, ...newState }
                                });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.NumberSettingInfoDiagram.titleExternal,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeExternalNumberModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        boardWidth={this.props.boardWidth}
                        getSoundName = {this.getSoundName}
                    />
                )}

                {/* 内線グループ詳細モーダル */}
                {this.state.showInboundGroupModal && (
                    <InboundGroupModal
                        state={this.state.inboundGroupModalData}
                        propSetState={(newState) => {
                            // モーダルが閉じられる場合
                            if (newState && newState.show === false) {
                                this.closeInboundGroupModal();
                            } else {
                                // データの更新があった場合は子モーダルのデータを更新
                                this.setState({
                                    inboundGroupModalData: { ...this.state.inboundGroupModalData, ...newState }
                                });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.NumberSettingInfoDiagram.titleInboundGroup,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeInboundGroupModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                    />
                )}

                {/* 内線番号詳細モーダル */}
                {this.state.showExtensionNumberModal && (
                    <ExtensionNumberModal
                        state={this.state.extensionNumberModalData}
                        propSetState={(newState) => {
                            // モーダルが閉じられる場合
                            if (newState && newState.show === false) {
                                this.closeExtensionNumberModal();
                            } else {
                                // データの更新があった場合は子モーダルのデータを更新
                                this.setState({
                                    extensionNumberModalData: { ...this.state.extensionNumberModalData, ...newState }
                                });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.NumberSettingInfoDiagram.titleExtension,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeExtensionNumberModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        getinboundGroupName={this.getinboundGroupName}
                    />
                )}

                {/* CSVダウンロード用の隠しリンク */}
                <CSVLink
                    data={this.state.csvData}
                    headers={this.state.csvHeader}
                    filename={this.getCsvFileName()}
                    className="csv-download-link-format hidden"
                    ref={this.csvLink}
                />
            </div>
        );
    }

    // 親画面の番号設定構成図モーダルを閉じる（共通化）
    closeNumberSettingInfoModal = () => {
        try {
            if (this.props && typeof this.props.propSetState === 'function') {
                this.props.propSetState({
                    showResourceMap: false,
                    resourceMapData: null,
                    resourceMapLoading: false
                });
            }
        } catch (e) {
            // noop
        }
    }
}

export default NumberSettingInfoDiagram;
